export type AnalyticsEvent = {
  event: "business_transaction_recorded" | "stock_item_created" | "stock_item_updated" | "pos_sale_recorded" | "sale_recorded" | "sales_return_recorded" | "purchase_recorded" | "purchase_return_recorded" | "purchase_order_recorded" | "quotation_recorded" | "restaurant_sale_recorded" | "exchange_recorded" | "stock_adjustment_recorded" | "client_payment_recorded" | "supplier_payment_recorded" | "shift_opened" | "shift_closed"
  transaction_type?: string
  record_id?: number
  total_amount?: number
}

const transactionEvents: Record<string, AnalyticsEvent["event"]> = {
  checkout: "pos_sale_recorded",
  sale: "sale_recorded",
  sales_return: "sales_return_recorded",
  purchase: "purchase_recorded",
  purchase_return: "purchase_return_recorded",
  purchase_order: "purchase_order_recorded",
  quotation: "quotation_recorded",
  restaurant_sale: "restaurant_sale_recorded",
  exchange: "exchange_recorded",
  stock_adjustment: "stock_adjustment_recorded",
  client_payment: "client_payment_recorded",
  supplier_payment: "supplier_payment_recorded",
}

export function granularTransactionEvent(type: string): AnalyticsEvent["event"] | undefined {
  return transactionEvents[type]
}

const staticRouteSegments = new Set(`
  auth login logout register refresh me mobile turnstile-config
  branches overview stock transfers tenants backups settings
  pos shifts open close current lookup business-date terminals held-bills tokens
  invoices next-ref by-ref convert customer-rate-history returns exchanges
  purchase-orders generate-from-reorder payments client-payments
  restaurant orders checkout waiters riders tables kitchen stations
  reports dashboard clients suppliers variants workers qz sign
  health adjustment adjustments import export status session connect
  group-types cleanup-legacy
`.trim().split(/\s+/))

/** Stable, low-cardinality route; never include query values or document identifiers. */
export function analyticsRoute(url: string | undefined): string | null {
  if (!url) return null
  try {
    const path = new URL(url, "https://analytics.invalid").pathname
    if (!path.startsWith("/api/")) return null
    return path.split("/").map((segment, index) =>
      index < 3 || staticRouteSegments.has(segment) ? segment : ":value",
    ).join("/")
  } catch {
    return null
  }
}

function requestBody(raw: unknown): Record<string, unknown> {
  if (typeof raw === "string") {
    try { return JSON.parse(raw) as Record<string, unknown> } catch { return {} }
  }
  return raw && typeof raw === "object" ? raw as Record<string, unknown> : {}
}

function recordId(data: Record<string, unknown>, key: string): number | undefined {
  const record = data[key]
  if (!record || typeof record !== "object") return undefined
  const id = Number((record as Record<string, unknown>).id)
  return Number.isInteger(id) && id > 0 ? id : undefined
}

function amount(data: Record<string, unknown>, key: string): number | undefined {
  const record = data[key]
  if (!record || typeof record !== "object") return undefined
  const value = Number((record as Record<string, unknown>).totalRs)
  return Number.isFinite(value) ? value : undefined
}

/** Classify completed API writes without sending request bodies or customer/item details. */
export function analyticsEventForMutation(
  method: string | undefined,
  url: string | undefined,
  rawBody: unknown,
  rawResponse: unknown,
): AnalyticsEvent | null {
  const verb = method?.toUpperCase()
  const path = (url ?? "").split("?")[0]
  const body = requestBody(rawBody)
  const data = rawResponse && typeof rawResponse === "object" ? rawResponse as Record<string, unknown> : {}
  const transaction = (transaction_type: string, key: string): AnalyticsEvent => ({
    event: "business_transaction_recorded",
    transaction_type,
    record_id: recordId(data, key),
    total_amount: amount(data, key),
  })

  // Keep the established general event for existing insights. Granular event names
  // are emitted alongside it by the capture layer, never by a second API call.

  if (verb === "POST" && path === "/api/invoices") {
    const source = String(body.source ?? "invoice")
    return transaction(source === "pos" ? "checkout" : source === "quotation" ? "quotation" : source === "return" ? "sales_return" : "sale", "invoice")
  }
  if (verb === "POST" && /^\/api\/invoices\/\d+\/convert$/.test(path)) return transaction("sale", "invoice")
  if (verb === "POST" && /^\/api\/pos\/held-bills\/\d+\/convert$/.test(path)) {
    return transaction(body.source === "pos" ? "checkout" : "sale", "invoice")
  }
  if (verb === "POST" && /^\/api\/restaurant\/orders\/\d+\/checkout$/.test(path)) return transaction("restaurant_sale", "invoice")
  if (verb === "POST" && path === "/api/purchase-orders/generate-from-reorder") return transaction("purchase_order", "order")
  if (verb === "POST" && path === "/api/purchase-orders") {
    const type = String(body.type ?? "order")
    return transaction(type === "purchase" ? "purchase" : type === "return" ? "purchase_return" : "purchase_order", "order")
  }
  if (verb === "POST" && /^\/api\/purchase-orders\/\d+\/convert$/.test(path)) return transaction("purchase", "order")
  if (verb === "PUT" && /^\/api\/purchase-orders\/\d+$/.test(path) && body.type === "purchase") return transaction("purchase", "order")
  if (verb === "POST" && path === "/api/stock/adjustments") return transaction("stock_adjustment", "adjustment")
  if (verb === "POST" && path === "/api/client-payments") return transaction("client_payment", "payment")
  if (verb === "POST" && path === "/api/payments") return transaction("supplier_payment", "payment")
  if (verb === "POST" && path === "/api/returns") return transaction("sales_return", "return")
  if (verb === "POST" && path === "/api/exchanges") return transaction("exchange", "invoice")
  if (verb === "POST" && path === "/api/pos/shifts/open") return { event: "shift_opened", record_id: Number(data.id) || undefined }
  if (verb === "POST" && /^\/api\/pos\/shifts\/\d+\/close$/.test(path)) return { event: "shift_closed", record_id: Number(data.id) || undefined }
  if (verb === "POST" && path === "/api/stock") return { event: "stock_item_created", record_id: recordId(data, "item") }
  if (verb === "PUT" && /^\/api\/stock\/\d+$/.test(path)) return { event: "stock_item_updated", record_id: recordId(data, "item") }
  return null
}
